/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { Alert, Fade, Icon, Snackbar } from '@mui/material'
import Card from '@mui/material/Card'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import { useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { updateRoutes, useMaterialUIController } from 'context'
import ManageExclusionForm from 'layouts/exclusion-list/manage-exclusion'
import { ExclusionForm } from 'layouts/exclusion-list/types'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'
import { ROUTE_PATHS, RouteType } from 'routes'
import { TabularData } from 'types/server/tabular'

import { postNewExclusion } from '../utils'

function ExclusionList(): JSX.Element {
	const [controller, dispatch] = useMaterialUIController()
	const { session, routes } = controller

	const [uploadStatus, setUploadStatus] = useState<{
		status: 'loading' | 'error' | 'success' | null
		message?: string
	}>(null)
	const navigate = useNavigate()
	const fileInputRef = useRef<HTMLInputElement | null>(null)

	const fetchData = async () => {
		const response = await axios.get<TabularData>(
			`${process.env.REACT_APP_API_URL}/exclusion-list`,
			{
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, error, isLoading } = useQuery({ queryKey: ['exclusion-list'], queryFn: fetchData })

	const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (fileInputRef.current) fileInputRef.current.value = ''
		if (!file) return

		if (file.type !== 'text/csv') {
			console.log('Unsupported file type:', file.type)
			setUploadStatus({ status: 'error', message: 'Only CSV files are supported.' })
			return
		}

		setUploadStatus(null)

		const reader = new FileReader()
		reader.onload = async () => {
			const text = reader.result as string
			// CSV content validation
			const lines = text.trim().split('\n')
			const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
			const requiredHeaders = ['phone_number', 'name', 'notes']

			// Check for required headers
			const hasAllHeaders = requiredHeaders.every(header => headers.includes(header))
			if (!hasAllHeaders) {
				setUploadStatus({
					status: 'error',
					message: 'CSV must contain the following headers: phone_number, name, notes.',
				})
				return
			}

			const numberRegex = /^\+44\d{10}$/

			Papa.parse(file, {
				header: true,
				skipEmptyLines: true,
				complete: async function (results) {
					const rows = results.data
					const exclusionList: ExclusionForm[] = []
					for (let i = 0; i < rows.length; i++) {
						const row = rows[i] as ExclusionForm

						if (!row['phone_number']) {
							setUploadStatus({
								status: 'error',
								message: `Phone number not valid for row ${i + 2}. Must be present.`,
							})
							return
						}

						if (!numberRegex.test(row['phone_number'])) {
							setUploadStatus({
								status: 'error',
								message: `Invalid status in row ${
									i + 2
								}. Must be a valid UK number,  starting with +44.`,
							})
							return
						}
						exclusionList.push({
							phone_number: row.phone_number,
							name: row.name,
							notes: row.notes,
						})
					}
					const success = await postNewExclusion(session.user, exclusionList)
					if (success) {
						setUploadStatus({
							status: 'success',
							message: `Successfully uploaded ${exclusionList.length} exclusions.`,
						})
					}
				},
			})
		}
		reader.readAsText(file)
	}

	function downloadCSV(filename = 'exclusion-list.csv') {
		const columns: (keyof ExclusionForm)[] = ['phone_number', 'name', 'notes']

		// map the data to only these fields
		const filtered = data.table.rows.map(row => {
			const out: any = {}
			columns.forEach(col => {
				out[col] = row[col]
			})
			return out
		})
		const csv = Papa.unparse(filtered)
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, filename)
	}

	useEffect(() => {
		if (data) {
			const allRows = data.table.rows
			const exclusionRows: RouteType[] = allRows.map(e => ({
				type: 'invisible' as const,
				name: `Exclusion Number: ${e.phone_number}`,
				key: `exclusion-number-${(e.phone_number as string).slice(1)}`,
				route: `${ROUTE_PATHS.EXCLUSION_LIST.MANAGE}/${(e.phone_number as string).slice(1)}`,
				component: <ManageExclusionForm />,
				authRequired: true,
			}))
			updateRoutes(dispatch, routes, exclusionRows)
		}
	}, [data])

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Snackbar
				open={uploadStatus?.status === 'success' || uploadStatus?.status === 'error'}
				autoHideDuration={5000}
				onClose={() => {}}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				TransitionComponent={Fade}
			>
				{uploadStatus?.status !== 'loading' && (
					<Alert severity={uploadStatus?.status} variant="standard">
						{uploadStatus?.message}
					</Alert>
				)}
			</Snackbar>
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox p={3} mb={1} lineHeight={1}>
						<MDTypography variant="h2" fontWeight="medium">
							Exclusion List
						</MDTypography>
						<MDTypography variant="button" color="text">
							View and manage all the phone numbers in your exclusion list.
						</MDTypography>
					</MDBox>
					<MDBox mx={3} display={'flex'} justifyContent={'space-between'} mb={2}>
						<MDButton
							color="primary"
							variant="outlined"
							onClick={() => navigate(ROUTE_PATHS.EXCLUSION_LIST.NEW)}
						>
							ADD NEW
							<Icon sx={{ ml: 1 }}>add_circle_outlined</Icon>
						</MDButton>
						<MDBox display={'flex'} justifyContent={'flex-end'} gap={3}>
							<MDInput
								ref={fileInputRef}
								accept=".csv"
								type="file"
								id="csv-exclusions-import"
								style={{ display: 'none' }}
								onChange={(e: ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
							/>
							<label htmlFor="csv-exclusions-import">
								<MDButton component="span" color="primary" variant="outlined">
									IMPORT
									<Icon sx={{ ml: 1 }}>upload</Icon>
								</MDButton>
							</label>
							<MDButton color="primary" variant="outlined" onClick={() => downloadCSV()}>
								EXPORT
								<Icon sx={{ ml: 1 }}>download</Icon>
							</MDButton>
						</MDBox>
					</MDBox>
					{!isLoading && !error ? (
						<DataTable
							table={data.table}
							canSearch
							parentPath={ROUTE_PATHS.EXCLUSION_LIST.MANAGE}
						/>
					) : (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					)}
				</Card>
			</MDBox>
		</DashboardLayout>
	)
}

export default ExclusionList
